import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import {
  companiesService,
  Company,
  CreateCompanyRequest,
  MaturityStage,
} from '../../services/companiesService'
import { ConfirmModal } from '../../components/ConfirmModal'

interface CompanyFormData {
  name: string
  trade_name: string
  cnpj: string
  maturity_stage: MaturityStage | ''
  is_active: boolean
  custom_min_mrr: string
  custom_min_margin: string
}

const emptyForm: CompanyFormData = {
  name: '',
  trade_name: '',
  cnpj: '',
  maturity_stage: '',
  is_active: true,
  custom_min_mrr: '',
  custom_min_margin: '',
}

interface EntryFormData {
  reference_month: string
  reference_year: string
  net_revenue: string
  net_margin: string
}

const currentYear = new Date().getFullYear()
const currentMonth = new Date().getMonth() + 1

const emptyEntryForm: EntryFormData = {
  reference_month: String(currentMonth),
  reference_year: String(currentYear),
  net_revenue: '',
  net_margin: '',
}

const maturityLabels: Record<string, string> = {
  startup: 'Startup',
  traction: 'Tração',
  growth: 'Growth',
  scale: 'Scale',
  mature: 'Madura',
}

const maturityPool: Record<string, string> = {
  startup: '30%',
  traction: '25%',
  growth: '22%',
  scale: '20%',
  mature: '18%',
}

const maturityColors: Record<string, string> = {
  startup: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  traction: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  growth: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  scale: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  mature: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
}

const defaultThresholds: Record<string, { mrr: string; margin: string }> = {
  startup: { mrr: '0', margin: '30' },
  traction: { mrr: '60000', margin: '30' },
  growth: { mrr: '150000', margin: '30' },
  scale: { mrr: '1000000', margin: '35' },
  mature: { mrr: '10000000', margin: '35' },
}

const monthNames = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return 'R$ 0,00'
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export const CompaniesPage = () => {
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // State
  const [search, setSearch] = useState('')
  const [filterMaturity, setFilterMaturity] = useState('')
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('')
  const [showForm, setShowForm] = useState(false)
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [formData, setFormData] = useState<CompanyFormData>(emptyForm)
  const [formError, setFormError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null)

  // Monthly entry modal state
  const [showEntryModal, setShowEntryModal] = useState(false)
  const [entryCompany, setEntryCompany] = useState<Company | null>(null)
  const [entryForm, setEntryForm] = useState<EntryFormData>(emptyEntryForm)
  const [entryError, setEntryError] = useState('')

  // Fetch all companies (for stats)
  const { data: allCompanies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesService.getAll(),
  })

  // Fetch filtered companies (for table)
  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['companies', search, filterMaturity, filterActive],
    queryFn: () =>
      companiesService.getAll({
        search: search || undefined,
        maturity_stage: filterMaturity || undefined,
        is_active: filterActive === '' ? undefined : filterActive === 'true',
      }),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCompanyRequest) => companiesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      closeForm()
    },
    onError: (error: any) => {
      setFormError(
        error.response?.data?.detail ||
          error.response?.data?.name?.[0] ||
          error.response?.data?.cnpj?.[0] ||
          JSON.stringify(error.response?.data) ||
          'Erro ao criar empresa.'
      )
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateCompanyRequest> }) =>
      companiesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      closeForm()
    },
    onError: (error: any) => {
      setFormError(
        error.response?.data?.detail ||
          error.response?.data?.name?.[0] ||
          error.response?.data?.cnpj?.[0] ||
          JSON.stringify(error.response?.data) ||
          'Erro ao atualizar empresa.'
      )
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => companiesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      setDeleteTarget(null)
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail ||
          'Erro ao excluir empresa. Verifique se não há funcionários vinculados.'
      )
      setDeleteTarget(null)
    },
  })

  // Monthly entry mutation
  const createEntryMutation = useMutation({
    mutationFn: ({ companyId, data }: { companyId: number; data: any }) =>
      companiesService.createMonthlyEntry(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      queryClient.invalidateQueries({ queryKey: ['monthly-entries'] })
      queryClient.invalidateQueries({ queryKey: ['quarterly-results'] })
      queryClient.invalidateQueries({ queryKey: ['company-dashboard'] })
      toast.success('Lançamento mensal salvo com sucesso! Resultado trimestral recalculado.')
      closeEntryModal()
    },
    onError: (error: any) => {
      setEntryError(
        error.response?.data?.detail ||
          error.response?.data?.reference_date?.[0] ||
          error.response?.data?.non_field_errors?.[0] ||
          JSON.stringify(error.response?.data) ||
          'Erro ao salvar lançamento.'
      )
    },
  })

  // Form handlers
  const openCreate = () => {
    setEditingCompany(null)
    setFormData(emptyForm)
    setFormError('')
    setShowForm(true)
  }

  const openEdit = (company: Company) => {
    setEditingCompany(company)
    setFormData({
      name: company.name,
      trade_name: company.trade_name,
      cnpj: company.cnpj,
      maturity_stage: company.maturity_stage,
      is_active: company.is_active,
      custom_min_mrr: company.custom_min_mrr || '',
      custom_min_margin: company.custom_min_margin || '',
    })
    setFormError('')
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingCompany(null)
    setFormData(emptyForm)
    setFormError('')
  }

  const openEntryModal = (company: Company) => {
    setEntryCompany(company)
    setEntryForm(emptyEntryForm)
    setEntryError('')
    setShowEntryModal(true)
  }

  const closeEntryModal = () => {
    setShowEntryModal(false)
    setEntryCompany(null)
    setEntryForm(emptyEntryForm)
    setEntryError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!formData.name.trim()) {
      setFormError('Nome da empresa é obrigatório.')
      return
    }
    if (!formData.maturity_stage) {
      setFormError('Estágio de maturidade é obrigatório.')
      return
    }

    const payload: CreateCompanyRequest = {
      name: formData.name.trim(),
      trade_name: formData.trade_name.trim() || undefined,
      cnpj: formData.cnpj.trim() || undefined,
      maturity_stage: formData.maturity_stage as MaturityStage,
      is_active: formData.is_active,
      custom_min_mrr: formData.custom_min_mrr.trim() || null,
      custom_min_margin: formData.custom_min_margin.trim() || null,
    }

    if (editingCompany) {
      updateMutation.mutate({ id: editingCompany.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const handleEntrySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setEntryError('')

    if (!entryCompany) return

    const mrr = parseFloat(entryForm.net_revenue)
    const margin = parseFloat(entryForm.net_margin)

    if (isNaN(mrr) || mrr <= 0) {
      setEntryError('Faturamento mensal (MRR) deve ser maior que zero.')
      return
    }
    if (isNaN(margin)) {
      setEntryError('Lucratividade é obrigatória.')
      return
    }

    const month = parseInt(entryForm.reference_month)
    const year = parseInt(entryForm.reference_year)
    const refDate = `${year}-${String(month).padStart(2, '0')}-01`

    createEntryMutation.mutate({
      companyId: entryCompany.id,
      data: {
        reference_date: refDate,
        net_revenue: mrr.toFixed(2),
        net_margin: margin.toFixed(2),
      },
    })
  }

  // Default thresholds hint for form
  const stageDefaults = formData.maturity_stage
    ? defaultThresholds[formData.maturity_stage]
    : null

  const isSaving = createMutation.isPending || updateMutation.isPending

  // Stats (always from all companies, not filtered)
  const stageCount = (stage: string) => allCompanies.filter((c) => c.maturity_stage === stage).length

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Empresas
        </h1>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors"
        >
          + Nova Empresa
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome, nome fantasia ou CNPJ..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="md:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Maturidade
            </label>
            <select
              value={filterMaturity}
              onChange={(e) => setFilterMaturity(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todas</option>
              <option value="startup">Startup (30%)</option>
              <option value="traction">Tração (25%)</option>
              <option value="growth">Growth (22%)</option>
              <option value="scale">Scale (20%)</option>
              <option value="mature">Madura (18%)</option>
            </select>
          </div>
          <div className="md:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as '' | 'true' | 'false')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="true">Ativas</option>
              <option value="false">Inativas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {allCompanies.length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Startup</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {stageCount('startup')}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{maturityLabels['traction']}</p>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {stageCount('traction')}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{maturityLabels['growth']}</p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {stageCount('growth')}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{maturityLabels['scale']}</p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {stageCount('scale')}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{maturityLabels['mature']}</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {stageCount('mature')}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Carregando empresas...</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🏢</div>
            <p className="text-gray-600 dark:text-gray-400">
              Nenhuma empresa encontrada.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    CNPJ
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Maturidade
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pool
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {company.name}
                        </p>
                        {company.trade_name && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {company.trade_name}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-700 dark:text-gray-300 text-sm font-mono">
                        {company.cnpj || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          maturityColors[company.maturity_stage] || ''
                        }`}
                      >
                        {maturityLabels[company.maturity_stage]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {maturityPool[company.maturity_stage]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          company.is_active
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        }`}
                      >
                        {company.is_active ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => navigate(`/empresas/${company.id}/dashboard`)}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Ver dashboard da empresa"
                        >
                          Dashboard
                        </button>
                        <button
                          onClick={() => openEntryModal(company)}
                          className="px-3 py-1 text-sm border border-primary rounded-btn text-primary hover:bg-primary hover:text-white transition-colors"
                          title="Lançar resultado mensal"
                        >
                          Lançar
                        </button>
                        <button
                          onClick={() => openEdit(company)}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => setDeleteTarget(company)}
                          className="px-3 py-1 text-sm border border-danger rounded-btn text-danger hover:bg-danger hover:text-white transition-colors"
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal — Create/Edit Company */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeForm} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {editingCompany ? 'Editar Empresa' : 'Nova Empresa'}
              </h3>

              {formError && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Razão Social *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Razão social da empresa"
                    required
                  />
                </div>

                {/* Trade Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome Fantasia
                  </label>
                  <input
                    type="text"
                    value={formData.trade_name}
                    onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Nome fantasia (opcional)"
                  />
                </div>

                {/* CNPJ */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    CNPJ
                  </label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 14)
                      const masked = digits
                        .replace(/^(\d{2})(\d)/, '$1.$2')
                        .replace(/^(\d{2}\.\d{3})(\d)/, '$1.$2')
                        .replace(/^(\d{2}\.\d{3}\.\d{3})(\d)/, '$1/$2')
                        .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{4})(\d)/, '$1-$2')
                      setFormData({ ...formData, cnpj: masked })
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                  />
                </div>

                {/* Maturity Stage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estágio de Maturidade *
                  </label>
                  <select
                    value={formData.maturity_stage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maturity_stage: e.target.value as CompanyFormData['maturity_stage'],
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  >
                    <option value="">Selecione o estágio</option>
                    <option value="startup">Startup - Pool 30%</option>
                    <option value="traction">Tração - Pool 25%</option>
                    <option value="growth">Growth - Pool 22%</option>
                    <option value="scale">Scale - Pool 20%</option>
                    <option value="mature">Madura - Pool 18%</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    O percentual do pool é definido automaticamente pelo estágio
                  </p>
                </div>

                {/* Ownership Configuration Section */}
                {formData.maturity_stage && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-card p-4 space-y-4">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                      Configuração Ownership
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Deixe em branco para usar os valores padrão da fase.
                    </p>

                    {/* Custom MRR */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        MRR Mínimo (Receita Líquida)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.custom_min_mrr}
                        onChange={(e) => setFormData({ ...formData, custom_min_mrr: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={`Padrão: ${formatCurrency(stageDefaults?.mrr || '0')}`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Padrão da fase: {formatCurrency(stageDefaults?.mrr || '0')}
                        {formData.maturity_stage === 'startup' && ' (não exigido)'}
                      </p>
                    </div>

                    {/* Custom Margin */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Margem Líquida Mínima (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.custom_min_margin}
                        onChange={(e) => setFormData({ ...formData, custom_min_margin: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={`Padrão: ${stageDefaults?.margin || '0'}%`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Padrão da fase: {stageDefaults?.margin || '0'}%
                        {formData.maturity_stage === 'startup' && ' (mínimo 30%)'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Active */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({ ...formData, is_active: e.target.checked })
                      }
                      className="rounded border-gray-300 text-primary focus:ring-primary w-5 h-5"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Empresa Ativa
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        Empresas inativas não aparecem nas opções de seleção
                      </p>
                    </div>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeForm}
                    disabled={isSaving}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving
                      ? 'Salvando...'
                      : editingCompany
                      ? 'Salvar Alterações'
                      : 'Criar Empresa'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Monthly Entry Modal */}
      {showEntryModal && entryCompany && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeEntryModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-lg w-full p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Lançamento Mensal
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {entryCompany.name} — {maturityLabels[entryCompany.maturity_stage]}
              </p>

              {entryError && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {entryError}
                </div>
              )}

              <form onSubmit={handleEntrySubmit} className="space-y-4">
                {/* Reference Period */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Mês *
                    </label>
                    <select
                      value={entryForm.reference_month}
                      onChange={(e) => setEntryForm({ ...entryForm, reference_month: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      {monthNames.slice(1).map((name, i) => (
                        <option key={i + 1} value={i + 1}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ano *
                    </label>
                    <select
                      value={entryForm.reference_year}
                      onChange={(e) => setEntryForm({ ...entryForm, reference_year: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* MRR */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Faturamento Mensal — MRR (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={entryForm.net_revenue}
                    onChange={(e) => setEntryForm({ ...entryForm, net_revenue: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Receita líquida do mês"
                    required
                  />
                </div>

                {/* Net Margin */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lucratividade Alcançada (%) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={entryForm.net_margin}
                    onChange={(e) => setEntryForm({ ...entryForm, net_margin: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Margem líquida do mês (pode ser negativa)"
                    required
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeEntryModal}
                    disabled={createEntryMutation.isPending}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createEntryMutation.isPending}
                    className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createEntryMutation.isPending ? 'Salvando...' : 'Salvar Lançamento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Excluir Empresa"
        description={
          <p>
            Tem certeza que deseja excluir <strong>{deleteTarget?.name}</strong>?
            Esta ação não pode ser desfeita e falhará se houver funcionários vinculados.
          </p>
        }
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        isDanger
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export default CompaniesPage
